import { bytesToMB } from './util';
import { formatAiQuotaCycleStart } from './aiQuotaUtils';
/**
 * Tính dung lượng còn lại và kiểm tra đủ để ghi hình
 */
export function freeStorage(customer): { remainingMB: string; isEnough: boolean } {
  const maxSize = customer?.maxStorageSizeAfterPurchase ?? customer?.maxStorageSize ?? 0;

  /// Quan trọng: Nếu maxSize là 0 (hệ thống chưa tính dung lượng của KH)
  /// => trả isEnough về true để không hiển thị cảnh báo dung lượng
  if (maxSize === 0) {
    return { remainingMB: '0MB', isEnough: true };
  }

  // Sử dụng Math.max để tránh hiển thị dung lượng âm
  const remaining = Math.max(0, customer?.remainingStorageSize ?? 0);

  const remainingMB = bytesToMB(remaining, true, 2) + "MB";
  const isEnough = !customer?.isStorageLowForRecording;

  return { remainingMB, isEnough };
}

/**
 * Hiển thị dialog xác nhận khi không đủ dung lượng để bắt đầu cuộc họp có ghi hình
 */
export async function confirmStartMeetingWithoutRecording(
  vueInstance: any,
  customer: any,
  onConfirm: () => Promise<void> | void,
  onOpenStorage: () => void
): Promise<void> {
  const h = vueInstance.$createElement;
  const freeStorageInfo = freeStorage(customer);

  const modalMessage = h('section', {
    style: {
      backgroundColor: '#fff5f5',
      padding: '20px',
      borderRadius: '12px',
      border: '1px solid #ffe3e3',
      borderLeft: '5px solid #e53e3e',
      boxShadow: '0 4px 12px rgba(229, 62, 62, 0.04)',
      fontFamily: 'inherit'
    }
  }, [
    h(
      'div',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          color: '#9b2c2c',
          fontSize: '17px',
          fontWeight: '700',
          marginBottom: '10px'
        }
      },
      [
        vueInstance.$t('notEnoughStorageToRecord').toString()
      ]
    ),

    h('div', {
      style: {
        fontSize: '14.5px',
        color: '#5c6470',
        lineHeight: '1.6',
        marginBottom: '8px'
      }
    }, [
      h(
        'span',
        vueInstance.$t('recordingStorageRequirement', {
          storage: freeStorageInfo.remainingMB
        }).toString()
      ),
      ' ',
      h('b', { style: { color: '#2c3e50' } }, vueInstance.$t('meetingNotRecordable').toString()),
      '.'
    ]),

    h('div', {
      style: {
        fontSize: '14px',
        color: '#5c6470'
      }
    }, [
    ]),

    h('div', {
      style: {
        fontSize: '14px',
        color: '#5c6470',
        marginTop: '6px'
      }
    }, [
      vueInstance.$t('storageCleanupPrompt').toString(),
      ' ',
      h(
        'a',
        {
          attrs: { href: 'javascript:void(0)' },
          style: {
            color: '#004f9e',
            fontWeight: '600',
            textDecoration: 'underline'
          },
          on: {
            click: (e: Event) => {
              e.preventDefault();
              onOpenStorage();
            }
          }
        },
        [h('strong', vueInstance.$t('here').toString())]
      )
    ])
  ]);

  try {
    const confirm = await vueInstance.$bvModal.msgBoxConfirm([modalMessage], {
      title: vueInstance.$t('alert').toString(),
      size: 'lg',
      okVariant: 'primary',
      okTitle: vueInstance.$t('startMeetingWithoutRecording').toString(),
      cancelTitle: vueInstance.$t('cancel').toString(),
      centered: true,
      returnFocus: document.body
    });
    if (confirm) {
      await onConfirm();
    }
  } catch (err) {
    console.error(err);
  } finally {
    (document.activeElement as HTMLElement)?.blur();
  }
}

/**
 * Hiển thị dialog xác nhận khi hết hạn mức AI để bắt đầu cuộc họp không sử dụng AI
 */
export async function confirmStartMeetingWithoutAI(
  vueInstance: any,
  cycleEnd: any,
  hasSummaryFeature: boolean,
  hasLiveCaptionFeature: boolean,
  onConfirm: () => Promise<void> | void
): Promise<void> {
  const h = vueInstance.$createElement;
  const formattedDate = formatAiQuotaCycleStart(cycleEnd);
  const modalMessage = h('section', {
    style: {
      backgroundColor: '#fff5f5',
      padding: '20px',
      borderRadius: '12px',
      border: '1px solid #ffe3e3',
      borderLeft: '5px solid #e53e3e',
      boxShadow: '0 4px 12px rgba(229, 62, 62, 0.04)',
      fontFamily: 'inherit'
    }
  }, [
    h(
      'div',
      {
        style: {
          display: 'flex',
          alignItems: 'center',
          color: '#9b2c2c',
          fontSize: '17px',
          fontWeight: '700',
          marginBottom: '10px'
        }
      },
      [
        vueInstance.$t('notEnoughAiFeatureLimit').toString()
      ]
    ),

    h('div', {
      style: {
        fontSize: '14.5px',
        color: '#5c6470',
        lineHeight: '1.6',
        marginBottom: '8px'
      }
    }, [
      h(
        'span',
        vueInstance.$t('quotaAIBelow10Percent').toString()
      )
    ]),

    h('div', {
      style: {
        fontSize: '14px',
        color: '#5c6470'
      }
    }, [
      vueInstance.$t('quotaRenewPrompt', { date: formattedDate }).toString()
    ])
  ]);

  try {
    const okTitle = hasSummaryFeature && hasLiveCaptionFeature
      ? vueInstance.$t('startMeetingWithoutAI').toString()
      : hasSummaryFeature
        ? vueInstance.$t('startMeetingWithoutAISummary').toString()
        : hasLiveCaptionFeature
          ? vueInstance.$t('startMeetingWithoutAILiveCaption').toString()
          : vueInstance.$t('startMeetingWithoutAI').toString();

    const confirm = await vueInstance.$bvModal.msgBoxConfirm([modalMessage], {
      title: vueInstance.$t('alert').toString(),
      size: 'lg',
      okVariant: 'primary',
      okTitle,
      cancelTitle: vueInstance.$t('cancel').toString(),
      centered: true,
      returnFocus: document.body
    });
    if (confirm) {
      await onConfirm();
    }
  } catch (err) {
    console.error(err);
  } finally {
    (document.activeElement as HTMLElement)?.blur();
  }
}

/**
 * Hiển thị dialog gom chung khi cả hạn mức AI lẫn dung lượng lưu trữ đều không đủ
 */
export async function confirmStartMeetingWithoutBoth(
  vueInstance: any,
  customer: any,
  onConfirm: () => Promise<void> | void,
  onOpenStorage: () => void
): Promise<void> {
  const h = vueInstance.$createElement;
  const cycleEnd = customer?.aiQuota?.cycleEnd;
  const formattedDate = formatAiQuotaCycleStart(cycleEnd);
  const freeStorageInfo = freeStorage(customer);

  const blockStyle = {
    backgroundColor: '#fff5f5',
    padding: '16px 20px',
    borderRadius: '10px',
    border: '1px solid #ffe3e3',
    borderLeft: '5px solid #e53e3e',
    boxShadow: '0 4px 12px rgba(229, 62, 62, 0.04)',
    fontFamily: 'inherit'
  };

  const titleStyle = {
    color: '#9b2c2c',
    fontSize: '16px',
    fontWeight: '700',
    marginBottom: '8px'
  };

  const bodyStyle = {
    fontSize: '14px',
    color: '#5c6470',
    lineHeight: '1.6',
    marginBottom: '4px'
  };

  const storageBlock = h('section', { style: { ...blockStyle, marginBottom: '12px' } }, [
    h('div', { style: titleStyle }, [vueInstance.$t('notEnoughStorageToRecord').toString()]),
    h('div', { style: bodyStyle }, [
      h('span', vueInstance.$t('recordingStorageRequirement', { storage: freeStorageInfo.remainingMB }).toString()),
      ' ',
      h('b', { style: { color: '#2c3e50' } }, vueInstance.$t('meetingNotRecordable').toString()),
      '.'
    ]),
    h('div', { style: { fontSize: '14px', color: '#5c6470' } }, [
      vueInstance.$t('storageCleanupPrompt').toString(),
      ' ',
      h('a', {
        attrs: { href: 'javascript:void(0)' },
        style: { color: '#004f9e', fontWeight: '600', textDecoration: 'underline' },
        on: { click: (e: Event) => { e.preventDefault(); onOpenStorage(); } }
      }, [h('strong', vueInstance.$t('here').toString())])
    ])
  ]);

  const aiBlock = h('section', { style: blockStyle }, [
    h('div', { style: titleStyle }, [vueInstance.$t('notEnoughAiFeatureLimit').toString()]),
    h('div', { style: bodyStyle }, [
      h('span', vueInstance.$t('quotaAIBelow10Percent').toString())
    ]),
    h('div', { style: { fontSize: '14px', color: '#5c6470' } }, [
      vueInstance.$t('quotaRenewPrompt', { date: formattedDate }).toString()
    ])
  ]);

  const modalContent = h('div', {}, [storageBlock, aiBlock]);

  try {
    const confirm = await vueInstance.$bvModal.msgBoxConfirm([modalContent], {
      title: vueInstance.$t('alert').toString(),
      size: 'lg',
      okVariant: 'primary',
      okTitle: vueInstance.$t('startMeetingWithoutRecordingAndAI').toString(),
      cancelTitle: vueInstance.$t('cancel').toString(),
      centered: true,
      returnFocus: document.body
    });
    if (confirm) {
      await onConfirm();
    }
  } catch (err) {
    console.error(err);
  } finally {
    (document.activeElement as HTMLElement)?.blur();
  }
}
